import type { Endgame } from "./types";
import { EXTRA_ENDGAMES } from "./endgames-extra";

/**
 * Essential endgames. Each one is played against the Lichess tablebase, which
 * defends perfectly. `npm run validate -- --network` checks that every starting
 * position really is the stated result with best play.
 */
const CORE_ENDGAMES: Endgame[] = [
  {
    slug: "queen-mate",
    category: "Basic mates",
    title: "King and queen against king",
    summary: "The first mate every player should know. Shrink the king's box with the queen, then bring your own king up to finish.",
    fen: "8/8/8/4k3/8/8/8/3QK3 w - - 0 1",
    side: "white",
    goal: "win",
    sections: [
      {
        heading: "The method",
        body: [
          "Use the queen to confine the enemy king to a shrinking rectangle. A queen a knight's move away from the king takes away the most squares without giving check.",
          "Once the king is on the edge, stop pushing with the queen. Walk your own king over until it stands opposite the enemy king, then mate on the edge.",
        ],
      },
      {
        heading: "The stalemate trap",
        body: [
          "The most common mistake is to keep shadowing the king with the queen after it reaches the corner. Before every move, check that the enemy king still has a legal move or is in check.",
        ],
      },
    ],
    keyIdeas: ["Box the king in", "Queen a knight's move away", "Bring your king", "Check for stalemate before every move"],
  },
  {
    slug: "rook-mate",
    category: "Basic mates",
    title: "King and rook against king",
    summary: "A rook can mate a lone king, but only with the help of its own king. The technique takes longer but follows a clear pattern.",
    fen: "8/8/8/4k3/8/8/8/R3K3 w - - 0 1",
    side: "white",
    goal: "win",
    sections: [
      {
        heading: "The method",
        body: [
          "Use the rook to cut the enemy king off along a rank or file. Bring your king toward it.",
          "When the kings stand opposite each other with one square between them, a rook check along the edge drives the enemy king back one more line. When the enemy king steps away from the opposition, make a waiting move with the rook along its line.",
          "Repeat until the king reaches the edge, then mate with the rook while your king covers the escape squares.",
        ],
      },
    ],
    keyIdeas: ["Cut the king off", "Kings in opposition", "Waiting moves with the rook", "Keep the rook far from the enemy king"],
  },
  {
    slug: "king-and-pawn-opposition",
    category: "Pawn endings",
    title: "King and pawn against king: the opposition",
    summary: "Whether a single pawn wins or draws often depends on one idea: the opposition. Get your king in front of the pawn and take the opposition.",
    fen: "8/8/4k3/8/4K3/8/4P3/8 w - - 0 1",
    side: "white",
    goal: "win",
    sections: [
      {
        heading: "Key squares",
        body: [
          "For a pawn that has not passed the middle of the board, the key squares are the three squares two ranks in front of it. If your king reaches one of them, the pawn promotes no matter what the defender does.",
          "Opposition means the kings stand on the same file with one square between them, and the side not to move holds it. The side with the opposition forces the other king to step aside.",
        ],
      },
      {
        heading: "The spare tempo",
        body: [
          "The kings face each other on e4 and e6, and it is White to move, so Black has the opposition. White's answer is a spare tempo: e2-e3. Now Black must move, the black king has to give way, and White's king reaches a key square.",
          "Compare the same position with the pawn already on e3. There is no spare tempo left, and with best defence the game is a draw. The tablebase confirms both results.",
          "The king moves Kd4 and Kf4 also win, but the natural-looking retreat Kd3 lets the win slip to a draw.",
        ],
      },
    ],
    keyIdeas: ["King in front of the pawn", "Take the opposition", "Use pawn moves as spare tempi", "Key squares two ranks ahead"],
  },
  {
    slug: "square-of-the-pawn",
    category: "Pawn endings",
    title: "The square of the pawn",
    summary: "Can the king catch a passed pawn? Draw the square from the pawn to the promotion rank. If the king can step into it, the pawn is caught.",
    fen: "7k/8/8/p7/8/5K2/8/8 w - - 0 1",
    side: "white",
    goal: "draw",
    sections: [
      {
        heading: "Drawing the square",
        body: [
          "Picture a square whose side runs from the pawn to its promotion square. Here the a5 pawn needs four moves to reach a1, so the square covers a1 to e5.",
          "If the defending king can move into that square, it catches the pawn. With White to move, any king step onto the e-file, such as Ke3, enters the square. A step the other way lets the pawn run.",
        ],
      },
      {
        heading: "Watch for the double step",
        body: [
          "A pawn on its starting rank can move two squares at once, so measure its square from one rank further forward.",
        ],
      },
    ],
    keyIdeas: ["Draw the square", "Step inside it", "Mind the pawn's double step"],
  },
  {
    slug: "lucena",
    category: "Rook endings",
    title: "The Lucena position: building a bridge",
    summary: "The most important winning position in rook endgames. Shelter your king from checks by building a bridge with your rook.",
    fen: "1K6/1P1k4/8/8/8/8/r7/2R5 w - - 0 1",
    side: "white",
    goal: "win",
    sections: [
      {
        heading: "The position",
        body: [
          "White's king stands in front of its own pawn on the seventh rank. Black's king is cut off on the d-file, and Black's rook waits to give checks from the side or from behind.",
        ],
      },
      {
        heading: "The bridge",
        body: [
          "First drive the black king one more file away with a rook check on the d-file. Then bring your rook to the fourth rank.",
          "When your king steps out and Black starts checking from behind, walk the king down the board toward your rook. When the king reaches the fifth rank, the rook interposes on the fourth: the bridge. The checks run out, and the pawn promotes.",
        ],
      },
    ],
    keyIdeas: ["Cut the king off first", "Rook to the fourth rank", "Walk the king toward the rook", "Block the last check"],
  },
  {
    slug: "philidor",
    category: "Rook endings",
    title: "The Philidor position: the third-rank defence",
    summary: "The key drawing method in rook endgames. Keep your rook on the third rank until the pawn advances, then check from behind.",
    fen: "4k3/R7/1r6/4K3/4P3/8/8/8 b - - 0 1",
    side: "black",
    goal: "draw",
    sections: [
      {
        heading: "The method",
        body: [
          "Your king stands in front of the pawn. Keep your rook on your third rank, here the sixth, so White's king cannot advance to it without walking into checks.",
          "As soon as White pushes the pawn to the sixth rank, the king loses its shelter in front of the pawn. Swing your rook to the first rank and check from behind. The king cannot escape the checks, and the game is drawn.",
        ],
      },
    ],
    keyIdeas: ["King in front of the pawn", "Rook on the third rank", "When the pawn advances, check from behind"],
  },
];

export const ENDGAMES: Endgame[] = [...CORE_ENDGAMES, ...EXTRA_ENDGAMES.map((x) => x.endgame)];

export function getEndgame(slug: string) {
  return ENDGAMES.find((e) => e.slug === slug);
}
